// Lib
import { hot } from 'react-hot-loader/root';
import React, { useRef } from 'react';
import classNames from 'classnames';
import { connect, ConnectedProps } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { compose } from '../../node_module_clones/recompose';

// Utils
import platformSingleton from '../platform/platformSingleton';
import useAttachEditingCommands from '../capacitor/hooks/useAttachEditingCommands';
import useVirtualKeyboardEventHandler from '../utils/keyboard/useVirtualKeyboardEventHandler';
import ScrollToCaretHandler from '../mobile/boardsPage/editing/ScrollToCaretHandler';

// Components
import AppHeader from './header/AppHeader';
import Toolbar from './toolbar/components/Toolbar';
import Main from './main/Main';
import WorkspacePositioner from './WorkspacePositioner';

import WorkspaceClipboardHandler from './shortcuts/clipboard/WorkspaceClipboardHandler';
import WorkspaceTooltipManager from '../components/tooltips/WorkspaceTooltipManager';
import CanvasContextMenuManager from './contextMenu/CanvasContextMenuManager';
import WorkspaceOnboardingManager from './onboarding/WorkspaceOnboardingManager';
import GlobalDragAndDropHelper from '../utils/dnd/GlobalDragAndDropHelper';
import GlobalPinchGestureBlocker from '../utils/ipad/GlobalPinchGestureBlocker';
import DesktopBoardTemplatePickerPopup from './boardTemplatePickerPopup/DesktopBoardTemplatePickerPopup';
import WorkspaceOnboardingUrlHandler from './onboarding/WorkspaceOnboardingUrlHandler';
import CurrentUserSubscriptionSynchroniser from '../user/CurrentUserSubscriptionSynchroniser';
import AnnotationEditor from '../element/drawing/annotation/annotationEditor/LoadableAnnotationEditor';
import PresentationModeFullScreenHandler from './presentation/PresentationModeFullScreenHandler';
import PresentationModeFocusLayer from './presentation/focusMode/PresentationModeFocusLayer';
import elementShortcutsDecorator from './shortcuts/clipboard/elementShortcutsDecorator';
import ToastRenderer from '../structural/toast/ToastRenderer';

// Selectors
import {
    isPresentationModeEnabledSelector,
    getIsPresentationModeEditingDisabled,
} from './presentation/presentationSelector';

// Context
import { ZoomStateContextProvider } from '../canvas/zoom/ZoomStateContext';

// Types
import { ApplicationType, DeviceFormFactor } from '../../common/platform/platformTypes';
import { GeneralWorkspaceProps } from './workspaceTypes';

// Styles
import './Workspace.scss';

const mapStateToProps = createStructuredSelector({
    isPresentationMode: isPresentationModeEnabledSelector,
    isEditingDisabled: getIsPresentationModeEditingDisabled,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector> & GeneralWorkspaceProps;

const Workspace = (props: Props) => {
    const {
        currentBoardId,
        currentBoard,
        rootBoardId,
        isCurrentBoardRoot,
        isCurrentBoardCached,
        isCurrentBoardFetching,
        isCurrentBoardFetched,
        isCurrentBoardDuplicating,
        isHierarchicalBoardError,
        isPresentationMode,
        isEditable,
        gridSize,
        gridSizeName,
        permissions,
        blockTemplateInteraction,
    } = props;

    const workspaceRef = useRef(null);

    const classes = {
        'workspace-root-board': isCurrentBoardRoot,
        presenting: isPresentationMode,
    };

    if (platformSingleton.formFactor !== DeviceFormFactor.desktop) {
        // The assumption here is that non-desktop devices use virtual keyboards
        useVirtualKeyboardEventHandler();
    }

    if (platformSingleton.appType === ApplicationType.ipadCapacitor) {
        // Listen for editing commands from the iPad app
        useAttachEditingCommands();
    }

    // If the current board is cached - don't show a loader or canvas contents
    const mainComponent = !isCurrentBoardCached ? <Main {...props} /> : null;

    return (
        <div className={classNames('DesktopWorkspace', 'Workspace', 'workspace-ready', classes)}>
            <ScrollToCaretHandler />
            <ZoomStateContextProvider zoomScale={1}>
                <AppHeader {...props} />
                <WorkspacePositioner
                    currentBoardId={currentBoardId}
                    isCurrentBoardFetching={isCurrentBoardFetching}
                    isCurrentBoardFetched={isCurrentBoardFetched}
                    isCurrentBoardDuplicating={isCurrentBoardDuplicating}
                    isHierarchicalBoardError={isHierarchicalBoardError}
                    ref={workspaceRef}
                >
                    <Toolbar
                        currentBoardId={currentBoardId}
                        gridSize={gridSize}
                        gridSizeName={gridSizeName}
                        permissions={permissions}
                        blockTemplateInteraction={blockTemplateInteraction}
                    />
                    <CanvasContextMenuManager
                        gridSize={gridSize}
                        currentBoardId={currentBoardId}
                        currentBoard={currentBoard}
                        blockTemplateInteraction={blockTemplateInteraction}
                        rootBoardId={rootBoardId}
                        isEditable={isEditable}
                        workspaceRef={workspaceRef}
                    >
                        {mainComponent}
                        {isPresentationMode && <PresentationModeFocusLayer currentBoardId={currentBoardId} />}
                    </CanvasContextMenuManager>
                </WorkspacePositioner>
                <DesktopBoardTemplatePickerPopup {...props} />
                <WorkspaceOnboardingManager />
                <WorkspaceOnboardingUrlHandler />
                <WorkspaceTooltipManager />
                <ToastRenderer />
                <WorkspaceClipboardHandler {...props} />
                <GlobalDragAndDropHelper />
                {platformSingleton.features.isTouch && <GlobalPinchGestureBlocker />}
                <CurrentUserSubscriptionSynchroniser />
                <AnnotationEditor />
                {isPresentationMode && <PresentationModeFullScreenHandler />}
            </ZoomStateContextProvider>
        </div>
    );
};

const enhance = compose<Props, GeneralWorkspaceProps>(hot, connector, elementShortcutsDecorator);

export default enhance(Workspace);
